import type { DailyCall } from '@daily-co/daily-js'
import { DailyProvider } from '@daily-co/daily-react'
import type { CurrentAdminRbacPermissionsResponse, PendingStoreKiosk } from '@gozoki/api-types'
import { AuthContext, type AuthContextType } from '../utils/context/auth.context'
import {
  ClientEntryContext,
  type ClientEntryContextType,
} from '../utils/context/clientEntry.context'

import { AbilityProvider } from '@casl/react'
import { type Right, hasOneRight, hasRights } from '@gozoki/tools'
import { ThemeProvider } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createAppAbility } from '../utils/ability'

import {
  attachSocketToken,
  getCurrentAdminRbacPermissionsOptions,
  setAiSocketUrl,
  setGozokiApiWebSocketManager,
  setStreamSocketUrl,
  useAdminMe,
} from '@gozoki/api'
import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { setToken } from '../utils/api'
import { VisioContext, type VisioContextType } from '../utils/context/visio.context'
import {
  parseRbacContract,
  rbacAbilityInput,
  rbacV2EnabledFromSession,
  resolveRbacSession,
} from '../utils/rbac-session'
import { materialTheme } from '../utils/theme/material.theme'

interface GlobalProviderProps {
  children: React.ReactNode
}

/** Cadence de la nouvelle tentative tant qu'aucun contrat exploitable n'est arrivé. */
const RBAC_RETRY_INTERVAL_MS = 15_000

/** Bits ouvrant le back-office tant que `permission-v2` est OFF. */
const LEGACY_BO_ACCESS_RIGHTS: Right[] = [
  'READER',
  'STOCK_READER',
  'ACCOUNTING_READER',
  'STORE_OPERATOR',
  'SUPPORT_OPERATOR',
  'ADMIN',
  'ACCOUNTING_ADMIN',
  'STOCK_ADMIN',
  'QUALITY',
]

const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const me = useAdminMe()
  const rbacPermissions = useQuery({
    ...getCurrentAdminRbacPermissionsOptions(),
    enabled: !!me.data,
    // Une réponse absente, en erreur ou incompatible ne bloque plus personne : le BO retombe sur
    // le régime hérité et retente tout seul jusqu'à obtenir un contrat exploitable (backend
    // déployé après le BO, 502 passager).
    retry: 3,
    refetchInterval: (query) =>
      parseRbacContract(query.state.data) === null ? RBAC_RETRY_INTERVAL_MS : false,
  })
  const [rememberedContract, setRememberedContract] =
    useState<CurrentAdminRbacPermissionsResponse | null>(null)
  const navigate = useNavigate()

  const [shouldStartCall, setShouldStartCall] = useState(false)
  const [kioskId, setKioskId] = useState<number | null>(null)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [includeVideo, setIncludeVideo] = useState(false)
  const [openingEventId, setOpeningEventId] = useState<number | null>(null)
  const [callLabel, setCallLabel] = useState<string | null>(null)
  const [isCallInProgress, setIsCallInProgress] = useState(false)
  const [callObject, setCallObject] = useState<null | DailyCall>(null)
  const callObjectRef = useRef<null | DailyCall>(null)
  const disposedCallObjects = useRef(new WeakSet<DailyCall>())
  const callReservedRef = useRef(false)

  const [pendingKiosks, setPendingKiosks] = useState<PendingStoreKiosk[]>([])

  const askCall = useCallback(
    (
      newKioskId: number,
      newStoreId: number,
      newIncludeVideo: boolean,
      newOpeningEventId?: number,
      newCallLabel?: string,
    ) => {
      if (callReservedRef.current || isCallInProgress || shouldStartCall) {
        return
      }

      callReservedRef.current = true
      setKioskId(newKioskId)
      setStoreId(newStoreId)
      setIncludeVideo(newIncludeVideo)
      setOpeningEventId(newOpeningEventId ?? null)
      setCallLabel(newCallLabel ?? null)
      setShouldStartCall(true)
    },
    [isCallInProgress, shouldStartCall],
  )

  const startCall = useCallback(() => {
    setShouldStartCall(false)
    setIsCallInProgress(true)
  }, [])

  const updateCallObject = useCallback((nextCallObject: DailyCall | null) => {
    callObjectRef.current = nextCallObject
    setCallObject(nextCallObject)
  }, [])

  const clearCallObject = useCallback(() => {
    const activeCallObject = callObjectRef.current
    callObjectRef.current = null
    setCallObject(null)
    if (!activeCallObject || disposedCallObjects.current.has(activeCallObject)) return

    disposedCallObjects.current.add(activeCallObject)
    void (async () => {
      try {
        await activeCallObject.leave()
      } catch {
        // Daily may already have left the room; destruction still has to run.
      }
      try {
        await activeCallObject.destroy()
      } catch {
        // Destruction is idempotent from the application's point of view.
      }
    })()
  }, [])

  const endCall = useCallback(() => {
    clearCallObject()
    setShouldStartCall(false)
    setKioskId(null)
    setStoreId(null)
    setOpeningEventId(null)
    setCallLabel(null)
    setIsCallInProgress(false)
    callReservedRef.current = false
  }, [clearCallObject])

  const askEntry = useCallback((newPendingKiosk: PendingStoreKiosk) => {
    setPendingKiosks((prevKiosks) => [...prevKiosks, newPendingKiosk])
  }, [])

  const openClientForPendingKiosk = useCallback(
    (processedKioskId: number, openPage: boolean) => {
      setPendingKiosks((prevKiosks) => {
        const kioskToProcess = prevKiosks.find((kiosk) => kiosk.kioskId === processedKioskId)

        if (kioskToProcess && openPage) {
          navigate({
            search: { storeId: kioskToProcess.storeId },
            to: '/cameras/new',
          })
        }
        return prevKiosks.filter((kiosk) => kiosk.kioskId !== processedKioskId)
      })
    },
    [navigate],
  )

  useEffect(() => {
    if (me?.data?.socketToken) {
      attachSocketToken(me.data.socketToken)
    }
    if (me?.data?.socketAiUrl) {
      setAiSocketUrl(me.data.socketAiUrl)
    }
    if (me?.data?.socketStreamUrl) {
      setStreamSocketUrl(me.data.socketStreamUrl)
    }
    if (me?.data?.socketUrl) {
      setGozokiApiWebSocketManager(me.data.socketUrl)
    }
  }, [me])

  const logout = useCallback(async () => {
    await setToken(null)
    await me.refetch()
    Sentry.setUser(null)
    navigate({ to: '/login' }) // Navigate back to login page
  }, [me, navigate])

  // `parseCurrentAdminRbacPermissionsResponse` renvoie la réponse elle-même : l'identité du
  // contrat suit celle de `rbacPermissions.data`, ce qui garde la mémorisation ci-dessous stable.
  const rbacSession = useMemo(
    () =>
      resolveRbacSession({
        hasUser: !!me.data,
        pending: rbacPermissions.isPending,
        error: rbacPermissions.isError,
        contract: parseRbacContract(rbacPermissions.data),
        remembered: rememberedContract,
      }),
    [
      me.data,
      rbacPermissions.isPending,
      rbacPermissions.isError,
      rbacPermissions.data,
      rememberedContract,
    ],
  )

  // Un compte dont une réponse exploitable est arrivée garde ce contrat pour la session : si la
  // requête suivante échoue, son ability v2 reste en place au lieu de retomber sur les bits.
  useEffect(() => {
    if (rbacSession.state === 'ready') setRememberedContract(rbacSession.contract)
  }, [rbacSession])

  const rbacData = rbacSession.contract
  const permissionV2Enabled = rbacV2EnabledFromSession(rbacSession)

  const abilityInput = useMemo(() => rbacAbilityInput(rbacData), [rbacData])
  const currentAbility = useMemo(() => {
    const { grants, grantsV2, ...flags } = abilityInput
    return createAppAbility(grants, grantsV2, flags)
  }, [abilityInput])

  const authContextValue: AuthContextType = useMemo(
    () => ({
      // Contrat annonçant le flag ON : zéro grant est une session BO valide, la racine affiche
      // l'état dédié avec déconnexion. Flag OFF, ou aucun contrat exploitable : le calcul legacy
      // (bits d'accès OU grants v1) fait foi, exactement comme sur `staging`.
      authenticated:
        !!me.data &&
        (permissionV2Enabled ||
          hasOneRight(me.data.adminRights, LEGACY_BO_ACCESS_RIGHTS) ||
          (rbacData?.grants.length ?? 0) > 0),
      user: me.data ?? null,
      refetchUser: me.refetch,
      isLoading: me.isLoading || (!!me.data && rbacPermissions.isLoading),
      logout,
      hasRights: (rights: Right[]) => !!me.data && hasRights(me.data.adminRights, rights),
      hasOneRight: (rights: Right[]) => !!me.data && hasOneRight(me.data.adminRights, rights),
      rbacLoading: !!me.data && rbacPermissions.isLoading,
      // Même source que l'ability installée ci-dessus : les deux ne peuvent plus diverger.
      rbacV2Enabled: permissionV2Enabled,
      rbacGrantsV2: abilityInput.grantsV2,
    }),
    [
      me.data,
      me.refetch,
      me.isLoading,
      logout,
      rbacPermissions.isLoading,
      rbacData,
      permissionV2Enabled,
      abilityInput.grantsV2,
    ],
  )

  const visioContextValue: VisioContextType = useMemo(
    () => ({
      shouldStartCall,
      kioskId,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      setCallObject: updateCallObject,
      clearCallObject,
      askCall,
      startCall,
      endCall,
    }),
    [
      kioskId,
      shouldStartCall,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      updateCallObject,
      clearCallObject,
      endCall,
      startCall,
      askCall,
    ],
  )

  const clientEntryContextValue: ClientEntryContextType = useMemo(
    () => ({
      pendingKiosks,
      askEntry,
      openClientForPendingKiosk,
    }),
    [pendingKiosks, askEntry, openClientForPendingKiosk],
  )

  if (me.isLoading || rbacSession.state === 'loading') {
    return null
  }

  return (
    <AbilityProvider value={currentAbility}>
      <AuthContext.Provider value={authContextValue}>
        <VisioContext.Provider value={visioContextValue}>
          <DailyProvider callObject={callObject}>
            <ClientEntryContext.Provider value={clientEntryContextValue}>
              <ThemeProvider theme={materialTheme}>
                {/* Bandeau discret, jamais bloquant : la requête retente d'elle-même. */}
                {rbacSession.degraded && (
                  <div
                    role="status"
                    className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center pb-3"
                  >
                    <span className="rounded-full border border-sidebar-border bg-background/95 px-3 py-1 text-muted-foreground text-xs shadow-xs">
                      Autorisations avancées indisponibles, nouvel essai…
                    </span>
                  </div>
                )}
                {children}
              </ThemeProvider>
            </ClientEntryContext.Provider>
          </DailyProvider>
        </VisioContext.Provider>
      </AuthContext.Provider>
    </AbilityProvider>
  )
}

export default GlobalProvider
